import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Car, Archive, Zap, Gauge } from "lucide-react";
import { fromKm, fmt, unitLabel } from "@/lib/units";
import { findColorByName } from "@/lib/teslaColors";
import { resolveImageUrl } from "@/lib/api";
import { useThemeColors } from "@/lib/theme";
import useVehicleOdometer, { odometerSourceLabel } from "@/lib/useVehicleOdometer";

function MiniTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--bg-input)] border border-[var(--border-subtle)] px-2 py-1" style={{ borderRadius: 2 }}>
      <div className="text-[10px] tracking-wider uppercase text-[var(--text-muted)]">{label}</div>
      <div className="font-mono-data text-xs text-[var(--text-primary)]">
        {fmt(payload[0].value, 1)} <span className="text-[var(--text-muted)]">{unitLabel(unit)}</span>
      </div>
    </div>
  );
}

export default function VehicleCard({ vehicle, stats, unit, onClick }) {
  const usedKm = stats?.year_used_km || 0;
  const limit = vehicle.yearly_limit_km || 0;
  const pct = limit > 0 ? Math.min(100, (usedKm / limit) * 100) : 0;
  const overLimit = usedKm > limit;
  const remainingKm = Math.max(0, limit - usedKm);
  const paint = findColorByName(vehicle.paint_name);

  const monthly = (stats?.monthly || []).map((m) => {
    const [y, mm] = m.month.split("-");
    const d = new Date(Number(y), Number(mm) - 1, 1);
    return {
      month: d.toLocaleString(undefined, { month: "short" }),
      label: d.toLocaleString(undefined, { month: "short", year: "2-digit" }),
      value: fromKm(m.distance_km, unit),
    };
  });

  const tripCount = stats?.trip_count || 0;
  const avgPerTripKm = stats?.avg_per_trip_km || 0;
  const tc = useThemeColors();

  // Authoritative current odometer — never derived here, always fetched
  // through the shared hook so OEM telemetry replaces this reading with
  // zero code changes the moment a provider is registered.
  const odo = useVehicleOdometer(vehicle.id);

  return (
    <button
      onClick={onClick}
      className={`group text-left bg-[var(--bg-canvas)] border transition-all overflow-hidden relative ${
        overLimit
          ? "border-[#E82127]/60 hover:border-[#E82127]"
          : "border-[var(--border-hover)] hover:border-[#E82127]/40"
      }`}
      style={{ borderRadius: 4 }}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      {/* Red bolt accent strip */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#E82127] opacity-70" />

      {/* Hero strip — light-tinted backdrop in light mode so PNG cars with
          black backgrounds don't sit on a harsh black strip. */}
      <div className="relative h-32 bg-[var(--bg-emphasis)] border-b border-[var(--border-hover)] overflow-hidden">
        {vehicle.image_url ? (
          <img
            src={resolveImageUrl(vehicle.image_url)}
            alt={vehicle.name}
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Car className="w-12 h-12 text-[var(--text-faint)]" strokeWidth={1} />
          </div>
        )}
        {/* Bottom gradient: always darkens to ensure the watermark text is
            readable over an arbitrary photo. Independent of theme — the photo
            content is the visual context, not the page surface. */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        {/* Red Tesla bolt corner badge */}
        <div className="absolute top-2 left-2 w-6 h-6 bg-[#E82127] flex items-center justify-center" style={{ borderRadius: 2 }}>
          <Zap className="w-3.5 h-3.5 text-[var(--text-primary)]" strokeWidth={2.5} />
        </div>
        {vehicle.archived && (
          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-md border border-white/15 px-2 py-0.5 flex items-center gap-1" style={{ borderRadius: 2 }}>
            <Archive className="w-3 h-3 text-white/80" strokeWidth={1.5} />
            <span className="text-[10px] tracking-wider uppercase text-white/80">Archived</span>
          </div>
        )}
        {overLimit && !vehicle.archived && (
          <div className="absolute top-2 right-2 bg-[#E82127] px-2 py-0.5 flex items-center gap-1" style={{ borderRadius: 2 }}>
            <span className="text-[10px] tracking-wider uppercase text-white font-medium">Over Limit</span>
          </div>
        )}
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/70 drop-shadow-sm">
              {vehicle.year || ""} {vehicle.make || ""}
            </div>
            <div className="font-display text-lg font-medium tracking-tight text-white drop-shadow-md">
              {vehicle.name}
            </div>
          </div>
          {paint && (
            <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-md border border-white/15 px-2 py-0.5" style={{ borderRadius: 2 }}>
              <span className="w-2 h-2 border border-white/30" style={{ background: paint.hex, borderRadius: 1 }} />
              <span className="text-[10px] text-white">{paint.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {/* Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="label-tiny">{stats?.year || new Date().getFullYear()}</span>
            <span className={`text-xs font-mono-data ${overLimit ? "text-[#FF5A5F]" : "text-[var(--text-primary)]"}`}>
              {pct.toFixed(1)}%
            </span>
          </div>
          <div className="h-1 bg-[var(--bg-hover)] overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${pct}%`,
                background: overLimit ? "#E82127" : "var(--text-primary)",
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 font-mono-data text-xs">
            <span className="text-[var(--text-primary)]">
              {fmt(fromKm(usedKm, unit), 0)} <span className="text-[var(--text-muted)]">used</span>
            </span>
            <span className="text-[var(--text-muted)]">
              {fmt(fromKm(remainingKm, unit), 0)} {unitLabel(unit)} left
            </span>
          </div>
        </div>

        {/* Current Odometer — resolved via OdometerService (shared hook).
            Shows a subtle source badge so users understand where the
            number came from (or "—" while the resolver has nothing yet). */}
        <div
          className="flex items-center justify-between pt-3 border-t border-[var(--border-hover)]"
          data-testid={`vehicle-card-odometer-${vehicle.id}`}
        >
          <div className="flex items-center gap-2">
            <Gauge className="w-3.5 h-3.5 text-[#E82127]" strokeWidth={1.75} />
            <span className="label-tiny">Odometer</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span
              className="font-mono-data text-sm text-[var(--text-primary)]"
              data-testid={`vehicle-card-odometer-value-${vehicle.id}`}
            >
              {odo.km == null || odo.km <= 0
                ? "—"
                : `${fmt(fromKm(odo.km, unit), 0)} ${unitLabel(unit)}`}
            </span>
            {odo.source && odo.source !== "none" && (
              <span className="text-[10px] tracking-wider uppercase text-[var(--text-muted)] font-mono-data">
                {odometerSourceLabel(odo.source)}
              </span>
            )}
          </div>
        </div>

        {/* Mini KPIs */}
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[var(--border-hover)]">
          <div>
            <div className="label-tiny mb-1">Total</div>
            <div className="font-mono-data text-sm text-[var(--text-primary)]">{fmt(fromKm(stats?.total_km || 0, unit), 0)}</div>
          </div>
          <div>
            <div className="label-tiny mb-1">Trips</div>
            <div className="font-mono-data text-sm text-[var(--text-primary)]">{tripCount}</div>
          </div>
          <div>
            <div className="label-tiny mb-1">Avg/Trip</div>
            <div className="font-mono-data text-sm text-[var(--text-primary)]">{fmt(fromKm(avgPerTripKm, unit), 1)}</div>
          </div>
        </div>

        {/* Mini monthly chart */}
        {monthly.length > 0 && (
          <div className="pt-3 border-t border-[var(--border-hover)]">
            <div className="flex items-center justify-between mb-1">
              <span className="label-tiny">Monthly · last {monthly.length}M</span>
              <span className="text-[10px] font-mono-data text-[var(--text-muted)]">
                {monthly[0].label} → {monthly[monthly.length - 1].label}
              </span>
            </div>
            <div className="h-14">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthly}
                  margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
                  onClick={(e) => e?.stopPropagation?.()}
                >
                  <Tooltip
                    cursor={{ fill: tc["bg-hover"] || tc["border-hover"] }}
                    content={(props) => <MiniTooltip {...props} unit={unit} />}
                  />
                  <XAxis
                    dataKey="month"
                    stroke={tc["text-muted"]}
                    tick={{ fill: tc["text-muted"], fontSize: 9, fontFamily: "JetBrains Mono" }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    height={14}
                  />
                  <Bar
                    dataKey="value"
                    fill={overLimit ? "#E82127" : tc["bg-inverse"]}
                    radius={[1, 1, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </button>
  );
}
